import React, { useEffect, useState } from 'react';
import { ModalWrapper } from './ModalWrapper';
import { meetingTextService } from '../../services/meetingTextService';
import { POPUP_STYLES } from '../../constants/popupStyles';

interface TranscriptPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  meetingId: number;
  textType: 'transcript' | 'rt_transcript';
  meetingTitle: string;
}

export const TranscriptPreviewModal: React.FC<TranscriptPreviewModalProps> = ({
  isOpen,
  onClose,
  meetingId,
  textType,
  meetingTitle,
}) => {
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !meetingId) return;

    const fetchTranscript = async () => {
      setLoading(true);
      setError(null);
      setContent(null);
      try {
        const texts = await meetingTextService.getMeetingTexts(meetingId, textType);
        if (texts.length > 0) {
          setContent(texts[0].content);
        } else {
          setContent('');
        }
      } catch (err) {
        setError('Failed to load transcript');
      } finally {
        setLoading(false);
      }
    };

    fetchTranscript();
  }, [isOpen, meetingId, textType]);

  const label = textType === 'rt_transcript' ? 'RT Transcript' : 'Transcript';

  return (
    <ModalWrapper
      isOpen={isOpen}
      onClose={onClose}
      title={`${label} — ${meetingTitle}`}
      isLoading={loading}
      width="!max-w-3xl"
    >
      <div className="px-6 pb-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#366A75] mx-auto"></div>
              <p className="text-[#366A75] mt-4 text-sm">Loading transcript...</p>
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-600">{error}</p>
          </div>
        ) : content === '' ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No transcript found</p>
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto border border-gray-200 rounded p-4 bg-gray-50">
            <p className="text-sm text-gray-800 whitespace-pre-wrap">{content}</p>
          </div>
        )}

        <div className="flex justify-center mt-6">
          <button onClick={onClose} className={POPUP_STYLES.button}>
            Close
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
};
